/**
 * Score Domain Store
 *
 * Manages investment readiness scores, scoring criteria, AI recommendations,
 * investor matches, and due diligence conclusions.
 */

import { create } from 'zustand';
import { calculateScoreCriteria } from '@/lib/scoring';
import { mode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';
import type { ScoreTrigger } from '@/lib/state/score-history-store';
import type {
  StartupProfile,
  InvestorProfile,
} from './profile-store';

// ============================================================================
// Types
// ============================================================================

// Scoring-domain types live in the scoring layer (`@/lib/scoring/types`). Imported
// here for internal use only — deliberately NOT re-exported, so each type has a
// single export site (its definition module). Importers must use the real homes:
// scoring types from `@/lib/scoring/types`, `StartupProfile` from `./profile-store`.
import type { ScoreCriteria, AIRecommendation } from '@/lib/scoring/types';

export interface InvestorMatch {
  id: string;
  investor: InvestorProfile;
  investorName: string;
  matchScore: number;
  matchReasons: string[];
  status: 'new' | 'contacted' | 'meeting_scheduled' | 'passed';
}

// ============================================================================
// Store Interface
// ============================================================================

/** LLM-generated 3-paragraph narrative attached to the latest score (optional). */
export interface ScoreSummaryNarrative {
  overall: string;
  strengths: string;
  weaknesses: string;
}

interface ScoreState {
  scoreCriteria: ScoreCriteria[];
  setScoreCriteria: (criteria: ScoreCriteria[]) => void;
  summaryNarrative: ScoreSummaryNarrative | null;
  setSummaryNarrative: (n: ScoreSummaryNarrative | null) => void;
  loadSampleScores: () => void;
  calculateScores: (startupProfile: StartupProfile | null) => void;
  triggerAIScoring: (trigger: ScoreTrigger, startupProfile: StartupProfile | null) => Promise<ScoreCriteria[]>;

  aiRecommendations: AIRecommendation[];
  setAIRecommendations: (recs: AIRecommendation[]) => void;
  addAIRecommendation: (rec: AIRecommendation) => void;
  removeAIRecommendation: (id: string) => void;

  investorMatches: InvestorMatch[];
  setInvestorMatches: (matches: InvestorMatch[]) => void;

  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  scoreCriteria: [] as ScoreCriteria[],
  summaryNarrative: null as ScoreSummaryNarrative | null,
  aiRecommendations: [] as AIRecommendation[],
  investorMatches: [] as InvestorMatch[],
};

// ============================================================================
// Store
// ============================================================================

export const useScoreStore = create<ScoreState>()(
  (set, get) => ({
    ...initialState,

    setScoreCriteria: (criteria) => set({ scoreCriteria: criteria }),

    setSummaryNarrative: (n) => set({ summaryNarrative: n }),

    loadSampleScores: () => {
      if (!mode.showSampleData) return;
      import('./mock-data-provider').then(({ getMockScoreCriteria }) => {
        set({ scoreCriteria: getMockScoreCriteria() });
      });
    },

    calculateScores: (startupProfile) => {
      if (!startupProfile) return;
      const calculatedCriteria = calculateScoreCriteria(startupProfile);
      set({ scoreCriteria: calculatedCriteria });
    },

    triggerAIScoring: async (trigger: ScoreTrigger, startupProfile: StartupProfile | null) => {
      const { useScoreHistoryStore } = await import('@/lib/state/score-history-store');
      const { profileToScoreInput, aiScoreToScoreCriteria } = await import('@/lib/scoring/ai-scoring');
      const { getAIScore } = await import('@/lib/supabase/edge-functions');
      const { mode: appMode } = await import('@/lib/config/app-mode');
      const { track } = await import('@/lib/analytics');

      track('score_refreshed', { trigger: trigger.type });

      const scoreHistoryStore = useScoreHistoryStore.getState();

      if (!startupProfile) {
        reportError('No startup profile available', { context: 'ScoreStore.triggerAIScoring', silent: true });
        return get().scoreCriteria;
      }

      // Guard: require minimum data before calling the AI scoring edge function.
      // Minimum = pitch deck extracted OR data room connected OR sufficient profile fields.
      const hasPitchDeckData = startupProfile.profileSource === 'deck';
      const hasDataRoom = !!startupProfile.dataRoomConnected;
      const hasDetailedProfile = !!(startupProfile.problem && startupProfile.solution);
      if (!hasPitchDeckData && !hasDataRoom && !hasDetailedProfile) {
        reportError('Insufficient data for scoring — skipping', { context: 'ScoreStore.triggerAIScoring', silent: true });
        return get().scoreCriteria;
      }

      scoreHistoryStore.setIsScoring(true);

      try {
        let newCriteria: ScoreCriteria[];

        if (appMode.isMock) {
          await new Promise((resolve) => setTimeout(resolve, 2000 + Math.random() * 1000));
          const baseCriteria = calculateScoreCriteria(startupProfile);
          newCriteria = baseCriteria.map((c) => ({
            ...c,
            score: Math.max(0, Math.min(100, c.score + Math.floor((Math.random() - 0.4) * 8))),
          }));
        } else {
          const input = profileToScoreInput(startupProfile);
          const response = await getAIScore({
            input,
            includeSubScores: true,
          });

          if (response.error || !response.data?.success || !response.data.data) {
            throw new Error(response.error || response.data?.error || 'AI scoring failed');
          }

          newCriteria = aiScoreToScoreCriteria(response.data.data.dimensions);
        }

        set({ scoreCriteria: newCriteria });

        const totalScore = newCriteria.reduce((sum, c) => sum + c.score, 0);
        const maxTotal = newCriteria.reduce((sum, c) => sum + c.maxScore, 0);
        const finalScore = Math.round((totalScore / maxTotal) * 100);

        const getReadinessLevel = (score: number) => {
          if (score >= 81) return 'excellent' as const;
          if (score >= 66) return 'strong' as const;
          if (score >= 46) return 'moderate' as const;
          if (score >= 26) return 'weak' as const;
          return 'critical' as const;
        };

        scoreHistoryStore.addSnapshot({
          timestamp: new Date(),
          finalScore,
          dimensions: newCriteria.map((c) => ({
            dimension: c.dimension || c.name,
            score: c.score,
            change: 0,
          })),
          trigger,
          readinessLevel: getReadinessLevel(finalScore),
        });

        scoreHistoryStore.setIsScoring(false);
        return newCriteria;
      } catch (error) {
        reportError(error, { context: 'ScoreStore.triggerAIScoring' });
        scoreHistoryStore.setScoringError(
          error instanceof Error ? error.message : 'An unexpected error occurred'
        );
        return get().scoreCriteria;
      }
    },

    setAIRecommendations: (recs) => set({ aiRecommendations: recs }),
    addAIRecommendation: (rec) => set({ aiRecommendations: [...get().aiRecommendations, rec] }),
    removeAIRecommendation: (id) => set({ aiRecommendations: get().aiRecommendations.filter((r) => r.id !== id) }),

    setInvestorMatches: (matches) => set({ investorMatches: matches }),

    reset: () => set(initialState),
  })
);
